import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { addExchangeOrderTagsFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  AddExchangeOrderTagsState,
  isAddExchangeOrderTagsStepValid,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import TagIcon from "@redotech/redo-web/icon-old/tag.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const ADD_EXCHANGE_ORDER_TAGS = (
  Details: React.ComponentType<StepTypeDetailsProps<AddExchangeOrderTagsState>>,
): StepType<AddExchangeOrderTagsState, ModelStep.AddExchangeOrderTags> => ({
  Details: (props: StepTypeDetailsProps<AddExchangeOrderTagsState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.ADD_EXCHANGE_ORDER_TAGS],
  valid(state) {
    return isAddExchangeOrderTagsStepValid(state).valid;
  },
  Icon: TagIcon,
  iconColor: "success",
  description(state) {
    if (state.tags.length > 0) {
      return t("Add exchange order tags: {{tags}}", {
        tags: state.tags.join(", "),
      });
    }
    return state.copyOriginalOrderTags
      ? t("Copy the original order's tags to the exchange order")
      : t("Add tags to the exchange draft order");
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, tags: [] },
  stepDeleted(state, stepId) {
    return produce(state, (draft) => {
      if (draft.next === stepId) {
        draft.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Add Exchange Order Tags step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.ADD_EXCHANGE_ORDER_TAGS,
      tags: state.tags,
      copyOriginalOrderTags: state.copyOriginalOrderTags,
      next: state.next ? id(state.next) : undefined,
    };
  },
  fromModel(model, id) {
    return addExchangeOrderTagsFromModel(model, id);
  },
});
